import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { CopyIcon } from '@phosphor-icons/react/dist/csr/Copy';

import { Button } from '../../design-system/primitives/Button/Button';
import styles from './TemporaryPassword.module.css';

/**
 * One password, shown once, with a copy control.
 *
 * REUSES `RecoveryCodeList.tsx`'s PATTERN, NOT ITS COMPONENT. That one shows
 * ten codes as a list with a `role="alert"` warning above them; this shows
 * one value, so the list markup would be one `<li>` wearing a `<ul>`'s
 * clothes. What carries over is the copy-with-fallback: `navigator.clipboard`
 * needs a secure context and a user gesture and is simply absent in jsdom, so
 * a rejected `writeText` is told to the user (`admin.copyFailed`) rather than
 * left as a button that appears to do nothing.
 *
 * NO `type="password"` FIELD. Hiding a value whose entire purpose is to be
 * read once and written down helps nobody — the handoff this follows is "read
 * it, don't guess it".
 *
 * THE CALLER OWNS DISMISSAL. This component holds no state that outlives one
 * render past `onAcknowledge` — there is no ref, no closure, nothing the
 * caller could reach after the value is gone. "Shown once" is a property of
 * the CALLER unmounting this component (typically by closing the `Dialog` it
 * sits in), not of anything in here; see `UserActions.tsx`.
 *
 * NAMES THE ACCOUNT (task 13b). This is the one dialog where reading the
 * wrong row is irreversible — a password copied and handed to the wrong
 * person — and every confirm dialog it follows already names `full_name` and
 * `username` (see `UserActions.tsx`'s three confirm bodies). `fullName` and
 * `username` are required rather than optional for the same reason `password`
 * is: a caller that forgot to pass them does not compile, instead of shipping
 * a password panel that names nobody.
 */

export interface TemporaryPasswordProps {
  /** Shown once. Never persisted, never logged, never in a URL. */
  password: string;
  /** The account this password belongs to. */
  fullName: string;
  /** Wrapped in `<bdi dir="ltr">` — a code identifier, not prose. */
  username: string;
  /** Dismissal. The parent decides what follows. */
  onAcknowledge: () => void;
}

export function TemporaryPassword({
  password,
  fullName,
  username,
  onAcknowledge,
}: TemporaryPasswordProps) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState<string | null>(null);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(password);
      setNotice(t('admin.copied'));
    } catch {
      // Not swallowed: a copy button that silently did nothing would leave
      // the administrator believing they had saved a password they have not.
      setNotice(t('admin.copyFailed'));
    }
  }

  return (
    <div className={styles['panel']}>
      <p className={styles['account']} data-testid="temporaryPasswordAccount">
        {fullName} <bdi dir="ltr">{username}</bdi>
      </p>

      <p className={styles['warning']} role="alert">
        {t('admin.temporaryPasswordOnce')}
      </p>

      {/*
        A code identifier, not prose: `<bdi dir="ltr">` so an Arabic page
        cannot mirror it, and tabular figures/wide tracking (the sheet) so it
        is read off one screen and typed into another correctly.
      */}
      <p className={styles['value']} data-testid="temporaryPasswordValue">
        <bdi dir="ltr">{password}</bdi>
      </p>

      <div className={styles['actions']}>
        <Button
          icon={<CopyIcon size={16} />}
          variant="secondary"
          onClick={() => {
            void copy();
          }}
        >
          {t('admin.copy')}
        </Button>

        <Button icon={<CheckIcon size={16} />} onClick={onAcknowledge}>{t('admin.dismiss')}</Button>
      </div>

      {/*
        Mounted from the first frame, empty — the copy result arrives later,
        and a region inserted with its text already in it gives assistive
        technology nothing to diff.
      */}
      <div
        className={styles['notice']}
        role="status"
        aria-atomic="true"
        data-testid="temporaryPasswordCopyNotice"
      >
        {notice}
      </div>
    </div>
  );
}
